import {
  queryOptions,
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import api from '@/admin/lib/api'
import { getPersistencePartitionKey } from '@/admin/lib/query-persistence/partition-key'

export type DataCaptureMode = 'EXCLUDED' | 'OPTIONAL' | 'COLLAPSED' | 'REQUIRED'

export type DataCaptureFieldKey =
  | 'consumer_email'
  | 'consumer_name'
  | 'consumer_phone'
  | 'consumer_address'
  | 'transaction_price'
  | 'transaction_date'
  | 'transaction_proof_of_purchase'

export interface FieldOverride {
  label?: string
  field_type?: string
}

export interface DataCaptureConfig {
  consumer_email: DataCaptureMode
  consumer_name: DataCaptureMode
  consumer_phone: DataCaptureMode
  consumer_address: DataCaptureMode
  transaction_price: DataCaptureMode
  transaction_date: DataCaptureMode
  transaction_proof_of_purchase: DataCaptureMode
  field_overrides?: Record<string, FieldOverride>
}

export type SegmentTemplateKind = 'product' | 'host' | 'transaction' | 'customer'

export interface SegmentTemplate {
  kind: SegmentTemplateKind
  uuid: string | null
  name: string
  attribute_count: number
  edit_url: string | null
  configured_via: string | null
}

export interface SegmentMetadata {
  templates: SegmentTemplate[]
}

export type ClaimsSearchMode = 'direct' | 'host_first'

/**
 * Per-tenant certificate toggles. A small list of booleans a tenant admin
 * flips on Settings -> Registration. Starts with `enable_download`; add more
 * keys here (and a row in the Certificate settings section) as the platform
 * grows more certificate options - the storage and API carry them generically.
 */
export interface CertificateSettings {
  /** Whether customers can view/download their warranty certificate. */
  enable_download: boolean
}

export const CERTIFICATE_SETTING_DEFAULTS: CertificateSettings = {
  enable_download: true,
}

/** One registration consent box as the tenant admin edits it. */
export interface ConsentBoxSettings {
  enabled: boolean
  /** Checkbox wording, Markdown (links allowed). */
  label: string
  /** Optional "how we use it" line shown with the box. */
  purpose: string
  /** T&C only. */
  required?: boolean
  /** T&C only, read-only: stamped when the wording changes. */
  version?: string
}

/** The tenant's registration consent boxes (T&C, marketing email). */
export type ConsentSettings = Record<string, ConsentBoxSettings>

export interface RegistrationSettings {
  segment_order: string[]
  enable_existing_host_selection: boolean
  /**
   * When true, the product step skips the category-card grid and shows
   * the product list directly. Selecting a parent product (a product
   * with variants) drills into a nested variant picker; selecting a
   * standalone product completes the step normally.
   */
  flat_product_selection: boolean
  registration_detail_view: 'property' | 'vehicle'
  claims_search_mode: ClaimsSearchMode
  /**
   * Whether the claim-intake surfaces name the specific registered item
   * alongside its product line ("Duration Premium - Driftwood") or show the
   * line alone. Display only: the registration still records exactly which
   * item was registered, and the claim's own Defect Details card still
   * carries the product and its colour.
   */
  show_product_variant_at_claim_intake: boolean
  early_capture_fields: string[]
  certificate_settings: CertificateSettings
  consent_settings?: ConsentSettings
  data_capture_config?: DataCaptureConfig
  segment_metadata?: Record<string, SegmentMetadata>
}

const DEFAULT_SETTINGS: RegistrationSettings = {
  segment_order: ['product', 'host', 'purchase', 'customer', 'review'],
  enable_existing_host_selection: false,
  flat_product_selection: false,
  registration_detail_view: 'property',
  claims_search_mode: 'direct',
  show_product_variant_at_claim_intake: true,
  early_capture_fields: [],
  certificate_settings: { ...CERTIFICATE_SETTING_DEFAULTS },
}

/**
 * User-facing noun for the host record a registration is attached to.
 * Driven by the tenant's `registration_detail_view` so copy reads
 * "property" for property tenants, "vehicle" for vehicle tenants, and a
 * neutral generic ("record") for anything else.
 */
export interface HostNoun {
  singular: string
  plural: string
  /** Hint shown under empty search states. */
  example: string
}

const HOST_NOUNS: Record<string, HostNoun> = {
  property: {
    singular: 'property',
    plural: 'properties',
    example: 'e.g. 123 Main St, or a postcode',
  },
  vehicle: {
    singular: 'vehicle',
    plural: 'vehicles',
    example: 'e.g. a VIN, plate, or owner name',
  },
}

const GENERIC_HOST_NOUN: HostNoun = {
  singular: 'registration',
  plural: 'registrations',
  example: 'e.g. an address, owner, or product',
}

export function hostNounForDetailView(
  detailView: string | null | undefined,
): HostNoun {
  return (detailView && HOST_NOUNS[detailView]) || GENERIC_HOST_NOUN
}

/**
 * True when the tenant's registrations hang off a host record (a property,
 * a vehicle) rather than standing alone. Host-based tenants identify a
 * registration by *where* it is, so surfaces that list registrations lead
 * with the host's address.
 */
export function isHostBasedDetailView(
  detailView: string | null | undefined,
): boolean {
  return Boolean(
    detailView && Object.prototype.hasOwnProperty.call(HOST_NOUNS, detailView),
  )
}

export const ALL_SEGMENTS = [
  'host_context',
  'customer',
  'host',
  'product',
  'purchase',
  'warranty',
  'registration_details',
  'review',
] as const

export type Segment = (typeof ALL_SEGMENTS)[number]

export const SEGMENT_LABELS: Record<string, string> = {
  product_type: 'Product Type',
  host_context: 'Property & Details',
  host_product: 'Host & Product',
  purchase_customer: 'Purchase & Customer',
  customer: 'Customer',
  host: 'Host',
  product: 'Product',
  purchase: 'Purchase',
  warranty: 'Warranty',
  registration_details: 'Information',
  review: 'Review',
}

// Exported as shared query options so the post-login cache warmer can prefetch
// this exact query without re-declaring the key/queryFn (no drift).
export function registrationSettingsQueryOptions() {
  return queryOptions<RegistrationSettings>({
    // Scoped to the active org/partner: settings fetched before a partner
    // context is chosen (the dealer's own org) must never drive the brand's wizard.
    queryKey: ['registration-settings', getPersistencePartitionKey()],
    queryFn: () =>
      api
        .get('/organisations/registration-settings/')
        .then((r) => r.data)
        .catch(() => DEFAULT_SETTINGS),
    staleTime: 10_000,
    placeholderData: DEFAULT_SETTINGS,
  })
}

export function useRegistrationSettings(options: { enabled?: boolean } = {}) {
  return useQuery<RegistrationSettings>({
    ...registrationSettingsQueryOptions(),
    enabled: options.enabled ?? true,
  })
}

export function useUpdateRegistrationSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<RegistrationSettings>) =>
      api.patch('/organisations/registration-settings/', data).then((r) => r.data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['registration-settings'] })
      // The wizard reads its consent boxes from the form schema.
      if (variables.consent_settings) {
        qc.invalidateQueries({ queryKey: ['holding-form-schema'] })
      }
    },
  })
}

/**
 * Whether the certificate download/print surfaces should be shown for the
 * current tenant. Reads the `enable_download` certificate toggle from
 * registration settings. Fails open (true) while settings are unknown so the
 * action never flickers away for tenants that have it enabled; a tenant that
 * has turned it off resolves to false once settings load, hiding every
 * certificate entry point. The consumer endpoint additionally enforces this
 * server-side.
 */
export function useCertificateDownloadEnabled(): boolean {
  const { data } = useRegistrationSettings()
  return data?.certificate_settings?.enable_download ?? true
}
